import React from 'react';
import classnames from 'classnames';

import {Button, Dialogue, TextField, Modal, addToast, Anchor} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';

import {TemplateModelV1} from 'resources/GeneratedModels/Template/TemplateModel.v1';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import {invalidateFilters} from 'components/PracticeView/PracticeViewToolbar/Filters/filters.utils';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {getFolderDetailPath} from 'client/Folders/foldersRouting';

interface Props {
  handleClose: () => void;
  onSaveSuccess: () => void;
}

interface State {
  newFolderName: string | null;
  hasError: boolean;
  errorMessage: string;
}

export default class NewFolderModal extends React.Component<Props, State> {
  static defaultProps = {
    onSaveSuccess: () => {}
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      newFolderName: null,
      hasError: false,
      errorMessage: ''
    };
  }

  folderNameInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      newFolderName: e.target.value
    });
  };

  resetErrorMessage = () => {
    this.setState({
      hasError: false,
      errorMessage: ''
    });
  };

  /**
   * Creates a new folder and invalidates the resource to force auto update
   */
  handleSave = async () => {
    const folderName = this.state.newFolderName;
    if (!folderName?.trim()) {
      this.setState({hasError: true, errorMessage: "Folder name can't be blank."});
      return;
    }

    if (folderName.length > 256) {
      this.setState({
        hasError: true,
        errorMessage: 'Folder name must be 256 characters or less.'
      });
      return;
    }

    try {
      const savedFolder = await new TemplateModelV1()
        .setName(folderName)
        .addRelationship({
          type: 'teacher_v1',
          relation: masqueradeStore.getUserIdByMasqueradeState()
        })
        .save();

      invalidatePartialInterest(resource('templates_v1').mandarkEndpoint(['templates_v1']).done());
      invalidateFilters();
      this.props.onSaveSuccess();
      // Outside the Folders view there's no other success cue for a created folder.
      const folderLink = (
        <Anchor
          underlined
          inheritColor
          target='_blank'
          rel='noopener noreferrer'
          href={getFolderDetailPath(savedFolder.getId())}
        >
          {folderName}
        </Anchor>
      );
      addToast({
        color: 'positive',
        title: 'Success!',
        message: <span>{folderLink} was added to your folders list.</span>
      });
    } catch {
      addToast({
        color: 'negative',
        title: 'Error:',
        message: 'Something went wrong and your folder was not created'
      });
    }
    this.props.handleClose();
  };

  render() {
    return (
      <Modal ariaLabel='Create New Folder Modal' handleClose={this.props.handleClose}>
        {({CloseButtonWrapper, modalContentStyle}) => {
          return (
            <Dialogue
              inModal
              hideCloseBtn
              className={classnames(modalContentStyle, 'folders-modal__new-folder-modal')}
            >
              <Dialogue.Body>
                <TextField
                  border
                  label='Enter a new folder name:'
                  error={this.state.hasError}
                  message={this.state.errorMessage}
                  onChange={this.folderNameInputHandler}
                  value={this.state.newFolderName}
                  placeholder='Type folder name here...'
                  onFocus={this.resetErrorMessage}
                  data-testid='new-folder-name-input'
                />
              </Dialogue.Body>
              <Dialogue.BtnGroup>
                <CloseButtonWrapper>
                  <Button variant='text'>Cancel</Button>
                </CloseButtonWrapper>
                <Button onClick={this.handleSave} data-testid='assignments_create-btn'>
                  Create
                </Button>
              </Dialogue.BtnGroup>
            </Dialogue>
          );
        }}
      </Modal>
    );
  }
}
